import { defineStore } from 'pinia';
import { ref, watch } from '@vue/composition-api';
import { isNil, isEqual } from 'lodash';
import { useInstance } from 'common-module/src/util/useInstance';
import { useGetter } from 'common-module/src/util/useVuex';

import { useLayoutAction } from './action';
import { useLayout, areaIdMap } from './layouts';
import { useDraggable } from './draggable';
import { useFields } from './field';

import { LayoutFieldItem } from '../model/layout';
import { saveLayout } from '../api/layout';
import { useEventBusEmit } from '../utils/useEventBus';

export const useFieldLayout = defineStore('fieldLayout', () => {
  const instance = useInstance();

  const actionStore = useLayoutAction();
  const layoutStore = useLayout();
  const draggableStore = useDraggable();

  const fieldStore = useFields(
    actionStore,
    layoutStore
  );

  const loading = ref(false);

  // 加载布局数据
  const reloadLayout = async () => {
    const { projectId, issueType } = actionStore.action.value || {};
    if (isNil(projectId) || isNil(issueType)) {
      return;
    }

    layoutStore.loadLayoutData(Number(projectId), issueType.id);
    layoutStore.loadUnusedField(Number(projectId), issueType.id);
  };
  watch(
    () => actionStore.drawerVisible.value,
    (val) => {
      val && reloadLayout();
    },
    { immediate: true }
  );

  // 保存布局
  const saveLoading = ref<boolean>(false);
  const onSave = async () => {
    const { projectId, issueType: { id: issueTypeId = null } = {} } = actionStore.action.value || {};
    if (isNil(projectId) || isNil(issueTypeId)) {
      return;
    }
    try {
      saveLoading.value = true;
      await saveLayout({
        filedList: getSaveFields(),
        issueTypeId,
        projectId,
        switches: getFeaturesParam()
      });
      instance.$message.success(instance.$t('release.Save_Successfully'));
      actionStore.closeDrawer();
      useEventBusEmit(instance, 'config-field-layout:save', issueTypeId);
    } catch (error) {
      console.error(error);
    } finally {
      saveLoading.value = false;
    }
  };

  // 功能开关处理
  const getFeaturesParam = () => {
    const switchesConfig = [
      {
        key: 'labour_record',
        on: () => {
          const { projectId } = actionStore.action.value || {};
          const labour = getFieldInfo(getSaveFields(), 'labourRecord');
          const switches = useGetter('features/getCurrentProjectFeature')?.('labour_record') || useGetter('features/getProjectFeature')?.('labour_record', projectId);
          return labour?.showed && !switches;
        }
      }
    ];
    return switchesConfig.map(s => ({
      key: s.key,
      on: (typeof s.on === 'function' ? s.on() : s.on)
    })).filter(s => Boolean(s.on));
  };

  const getFieldInfo = (fields: LayoutFieldItem[] = [], code: string) => {
    return (fields || []).find(item => item.fieldCode === code);
  };

  // 获取（处理）保存字段
  const getSaveFields = () => {
    const leftFields = batchUpdateFieldData(layoutStore.leftAreaFields.value, { areaId: areaIdMap.left });
    const rightFields = batchUpdateFieldData(layoutStore.rightAreaFields.value, { areaId: areaIdMap.right });

    // 被删除字段
    const deletedFields = layoutStore.oldLayoutFields.value.filter(o =>
      layoutStore.unusedAreaFields.value.some(f => f.customFieldId === o.customFieldId)
    ).map(f => ({ ...f, removed: true }));

    // 不在布局显示的字段
    const ignoreFields = layoutStore.ignoreFields.value.map(f => ({ ...f, removed: false }));

    return [
      ...leftFields,
      ...rightFields,
      ...ignoreFields,
      ...deletedFields
    ];
  };

  // 批量处理提交字段数据
  const batchUpdateFieldData = (
    fields: LayoutFieldItem[],
    attributeData: Object
  ): LayoutFieldItem[] => {
    return fields.map((field, idx) => ({
      ...field,
      ...attributeData,
      ordinate: idx,
      removed: false
    }));
  };

  const checkChanged = () => {
    const newFields = getSaveFields().map((i, idx) => ({ ...i, ordinate: idx }));
    const oldFields = (layoutStore.oldLayoutFields.value || []).map((f, idx) => ({ ...f, ordinate: idx, removed: false }));

    return !isEqual(newFields, oldFields);
  };

  return {
    loading,
    saveLoading,
    ...actionStore,
    ...layoutStore,
    ...draggableStore,
    ...fieldStore,
    onSave,
    checkChanged
  };
});
